import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Megaphone, Mail, MessageSquare, Send, Sparkles, Users, AlertTriangle, History } from 'lucide-react';
import { MARKETING_PRESETS, PRESET_GROUPS, renderMarketingPreview } from '@/lib/marketingPresets';
import { useTenantBranding } from '@/contexts/TenantBrandingContext';
import { toBrandName } from '@/utils/brandName';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Firmennamen ohne Rechtsformzusatz (GmbH/AG…) → saubere Team-Signatur.
// Platzhalter, die pro Empfänger ersetzt werden.
const PLACEHOLDERS = ['{first_name}', '{booking_link}', '{apero_date}'];

const MarketingCampaignComposer = () => {
  const { companyName, name: brandNameRaw, companyLogo, logoUrl } = useTenantBranding();
  const brandName = toBrandName(companyName || brandNameRaw);
  const brandLogo = companyLogo || logoUrl || '';
  const [channels, setChannels] = useState([]);        // Zielgruppen aus /crm/audience-channels
  const [selectedSources, setSelectedSources] = useState([]); // MEHRERE Zielgruppen
  const [onlyNew, setOnlyNew] = useState(false);
  const [studios, setStudios] = useState([]);          // Studios für den Standort-Filter
  const [studioIds, setStudioIds] = useState([]);      // angehakte Studios (leer bis Laden)
  const [combined, setCombined] = useState({ email_recipients: 0, whatsapp_recipients: 0, total: 0 });

  const [sendEmail, setSendEmail] = useState(true);
  const [sendWhatsapp, setSendWhatsapp] = useState(false);

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [bookingLink, setBookingLink] = useState('https://strongfit.ch/buchen');
  const [aperoDate, setAperoDate] = useState('');

  const [waTemplates, setWaTemplates] = useState([]);
  const [waTemplateName, setWaTemplateName] = useState('');
  const [waLanguage, setWaLanguage] = useState('de');
  const [waParams, setWaParams] = useState([]);        // Werte für {{1}}, {{2}} …

  const [aiPrompt, setAiPrompt] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const [testEmail, setTestEmail] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  const [sending, setSending] = useState(false);

  const [history, setHistory] = useState([]);

  // Alle Studios angehakt = kein Filter (schliesst auch Kunden ohne Studio ein).
  const effectiveStudioIds = useMemo(() => (
    (studios.length === 0 || studioIds.length === studios.length) ? [] : studioIds
  ), [studios, studioIds]);
  const studioParam = effectiveStudioIds.join(',');

  const loadChannels = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/crm/audience-channels`, {
        headers: authHeaders(), params: studioParam ? { studio_ids: studioParam } : {},
      });
      const list = res.data?.channels || [];
      setChannels(list);
      setSelectedSources((prev) => (prev.length ? prev : (list.find((c) => c.key === 'kunden_newsletter') ? ['kunden_newsletter'] : (list[0] ? [list[0].key] : []))));
    } catch (e) { /* silent */ }
  }, [studioParam]);

  const loadStudios = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/studios`, { headers: authHeaders() });
      const list = Array.isArray(res.data) ? res.data : (res.data?.studios || []);
      setStudios(list);
      setStudioIds(list.map((s) => s.id)); // Standard: alle Studios angehakt
    } catch (e) { setStudios([]); }
  }, []);

  const loadWaTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/whatsapp/templates?status=APPROVED&limit=200`, { headers: authHeaders() });
      setWaTemplates(res.data?.data || []);
    } catch (e) { setWaTemplates([]); }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/crm/interest-leads/campaigns`, { headers: authHeaders() });
      setHistory(res.data?.campaigns || []);
    } catch (e) { /* silent */ }
  }, []);

  const loadCombined = useCallback(async () => {
    if (!selectedSources.length) { setCombined({ email_recipients: 0, whatsapp_recipients: 0, total: 0 }); return; }
    try {
      const res = await axios.get(`${API}/crm/campaign/preview-count`, {
        headers: authHeaders(),
        params: { sources: selectedSources.join(','), studio_ids: studioParam, only_new: onlyNew },
      });
      setCombined(res.data || { email_recipients: 0, whatsapp_recipients: 0, total: 0 });
    } catch (e) { /* silent */ }
  }, [selectedSources, studioParam, onlyNew]);

  useEffect(() => { loadStudios(); loadWaTemplates(); loadHistory(); }, [loadStudios, loadWaTemplates, loadHistory]);
  useEffect(() => { loadChannels(); }, [loadChannels]);
  useEffect(() => { loadCombined(); }, [loadCombined]);

  const toggleSource = (key) => setSelectedSources((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  const toggleStudio = (id) => setStudioIds((prev) => (prev.includes(id) ? prev.filter((k) => k !== id) : [...prev, id]));

  const emailCount = combined.email_recipients ?? 0;
  const phoneCount = combined.whatsapp_recipients ?? 0;
  const audienceLabel = useMemo(() => (
    selectedSources.map((k) => channels.find((c) => c.key === k)?.label || k).join(' + ') || '—'
  ), [selectedSources, channels]);

  const selectedTemplate = useMemo(() => waTemplates.find((t) => t.name === waTemplateName), [waTemplates, waTemplateName]);
  const waBodyText = useMemo(() => selectedTemplate?.components?.find((c) => c.type === 'BODY')?.text || '', [selectedTemplate]);
  const waRequiredParams = useMemo(() => (waBodyText.match(/{{\d+}}/g) || []).length, [waBodyText]);

  const onSelectTemplate = (name) => {
    setWaTemplateName(name);
    const tpl = waTemplates.find((t) => t.name === name);
    if (tpl?.language) setWaLanguage(tpl.language);
    const bodyC = tpl?.components?.find((c) => c.type === 'BODY');
    const text = bodyC?.text || '';
    const count = (text.match(/{{\d+}}/g) || []).length;
    const example = bodyC?.example?.body_text?.[0] || [];
    setWaParams(Array.from({ length: count }, (_, i) => example[i] || (i === 0 ? '{first_name}' : '')));
  };

  const applyPreset = (preset) => {
    // Alle "StrongFit"-Vorkommen → aktiver Betriebsname (StrongFit bleibt StrongFit).
    const personalize = (txt) => (txt || '').split('StrongFit').join(brandName);
    setSubject(personalize(preset.subject));
    setBody(personalize(preset.html));
    setShowPreview(true);
    toast.success(`Vorlage „${preset.label}" eingefügt – prüfe die Vorschau.`);
  };

  const handleAi = async () => {
    if (!aiPrompt.trim()) { toast.error('Beschreibe zuerst, was erstellt werden soll'); return; }
    setAiBusy(true);
    try {
      const res = await axios.post(`${API}/newsletters/ai-template`, { prompt: aiPrompt }, { headers: authHeaders() });
      if (res.data?.email) {
        setSubject(res.data.email.subject || '');
        setBody(res.data.email.html_body || '');
        setShowPreview(true);
      }
      toast.success('✨ E-Mail-Vorlage im StrongFit-Layout erstellt');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'KI-Erstellung fehlgeschlagen');
    } finally { setAiBusy(false); }
  };

  const aperoMissing = () => {
    const usesApero = [subject, body, ...(waParams || [])].some((t) => (t || '').includes('{apero_date}'));
    return usesApero && !aperoDate.trim();
  };

  const buildPayload = () => ({
    sources: selectedSources,
    source: selectedSources[0] || 'alle',
    send_email: sendEmail,
    email_subject: subject,
    email_body: body,
    send_whatsapp: sendWhatsapp,
    whatsapp_template_name: sendWhatsapp ? waTemplateName : null,
    whatsapp_language: waLanguage || 'de',
    whatsapp_params: sendWhatsapp ? (waParams || []).slice(0, waRequiredParams) : [],
    booking_link: bookingLink,
    apero_date: aperoDate,
    only_new: onlyNew,
    studio_ids: effectiveStudioIds,
  });

  const handleTest = async () => {
    if (!testEmail.trim()) { toast.error('Bitte eine Test-E-Mail-Adresse eingeben'); return; }
    if (!subject.trim() || !body.trim()) { toast.error('Betreff und Inhalt ausfüllen'); return; }
    if (aperoMissing()) { toast.error('Bitte das 🥂 Eröffnungs-Apéro-Datum eingeben – die Vorlage enthält {apero_date}.'); return; }
    setTestBusy(true);
    try {
      const res = await axios.post(`${API}/crm/interest-leads/campaign/test`, buildPayload(), {
        headers: authHeaders(), params: { test_email: testEmail.trim() },
      });
      toast.success(res.data?.message || 'Test-Mail gesendet');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test-Versand fehlgeschlagen');
    } finally { setTestBusy(false); }
  };

  const handleSend = async () => {
    if (!selectedSources.length) { toast.error('Bitte mindestens eine Zielgruppe wählen'); return; }
    if (!sendEmail && !sendWhatsapp) { toast.error('Mindestens einen Kanal (E-Mail oder WhatsApp) wählen'); return; }
    if (sendEmail && (!subject.trim() || !body.trim())) { toast.error('Betreff und Inhalt ausfüllen'); return; }
    if (sendWhatsapp && !waTemplateName) { toast.error('Für WhatsApp eine genehmigte Vorlage wählen'); return; }
    if (aperoMissing()) { toast.error('Bitte das 🥂 Eröffnungs-Apéro-Datum eingeben – die Vorlage enthält {apero_date}.'); return; }

    const estimate = sendEmail ? emailCount : phoneCount;
    if (!window.confirm(`Kampagne jetzt an „${audienceLabel}" senden (${estimate} Empfänger, dedupliziert)? Der Versand läuft im Hintergrund.`)) return;

    setSending(true);
    try {
      const res = await axios.post(`${API}/crm/interest-leads/campaign`, buildPayload(), { headers: authHeaders() });
      toast.success(res.data?.message || 'Kampagne gestartet');
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kampagne konnte nicht gestartet werden');
    } finally { setSending(false); }
  };

  const insertPlaceholder = (ph) => setBody((b) => `${b}${ph}`);

  return (
    <div className="space-y-6" data-testid="marketing-composer">
      {/* 1. Zielgruppe */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-teal-600" /> 1. Zielgruppe wählen</CardTitle>
          <CardDescription>Wähle eine ODER mehrere Zielgruppen. Doppelte Empfänger (z.B. Lead + Kunde) werden automatisch nur einmal angeschrieben.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {studios.length > 1 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="marketing-studio-filter">
              <Label className="text-xs shrink-0">🏢 Standorte (nur Kunden-Zielgruppen):</Label>
              {studios.map((s) => (
                <label key={s.id} className="flex items-center gap-1.5 text-sm px-2 py-1 rounded-md border cursor-pointer" data-testid={`marketing-studio-${s.id}`}>
                  <input type="checkbox" checked={studioIds.includes(s.id)} onChange={() => toggleStudio(s.id)} />
                  {s.name}
                </label>
              ))}
              {effectiveStudioIds.length > 0 && <Badge variant="outline" className="text-[10px]">gefiltert</Badge>}
            </div>
          )}
          <div className="grid gap-2 sm:grid-cols-2" data-testid="marketing-audience-list">
            {channels.map((c) => {
              const active = selectedSources.includes(c.key);
              return (
              <label
                key={c.key}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${active ? 'border-teal-600 bg-teal-50/60 ring-1 ring-teal-600' : 'border-gray-200 hover:border-gray-300'}`}
                data-testid={`marketing-audience-${c.key}`}
              >
                <input type="checkbox" className="mt-1" checked={active} onChange={() => toggleSource(c.key)} />
                <div className="min-w-0">
                  <div className="text-sm font-medium">{c.label}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    📧 {c.count} E-Mail{typeof c.phone_count === 'number' ? ` · 📱 ${c.phone_count} WhatsApp` : ''}
                  </div>
                  {c.warn && (
                    <div className="text-[11px] text-amber-700 mt-1 flex items-start gap-1">
                      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {c.warn}
                    </div>
                  )}
                </div>
              </label>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={onlyNew} onCheckedChange={setOnlyNew} data-testid="marketing-only-new-switch" />
              Nur an Neue senden (bereits kontaktierte Empfänger überspringen)
            </label>
            {selectedSources.length > 0 && (
              <span className="text-xs text-teal-800 bg-teal-50 border border-teal-200 rounded-full px-3 py-1" data-testid="marketing-combined-count">
                Ausgewählt: <strong>{audienceLabel}</strong> → dedupliziert 📧 <strong>{emailCount}</strong> · 📱 <strong>{phoneCount}</strong>
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 2. Kanäle */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Megaphone className="w-5 h-5 text-teal-600" /> 2. Kanäle</CardTitle>
          <CardDescription>Wähle einen oder beide Versandkanäle. Die Zahlen sind die deduplizierte Gesamtzahl der gewählten Zielgruppen.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={sendEmail} onCheckedChange={setSendEmail} data-testid="marketing-channel-email" />
            <Mail className="w-4 h-4" /> E-Mail <span className="text-gray-400">({emailCount})</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={sendWhatsapp} onCheckedChange={setSendWhatsapp} data-testid="marketing-channel-whatsapp" />
            <MessageSquare className="w-4 h-4" /> WhatsApp <span className="text-gray-400">({phoneCount})</span>
          </label>
        </CardContent>
      </Card>

      {/* 3. Inhalt */}
      {sendEmail && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-teal-600" /> 3. E-Mail-Inhalt</CardTitle>
            <CardDescription>KI-Generator, fertige Vorlagen oder eigener HTML-Text. Platzhalter werden pro Empfänger ersetzt.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* KI */}
            <div className="p-3 rounded-lg border border-indigo-200 bg-indigo-50/50 space-y-2" data-testid="marketing-ai-box">
              <Label className="text-xs text-indigo-700 font-semibold flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> KI-Vorlagen-Designer</Label>
              <div className="flex gap-2">
                <Input value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} placeholder="z.B. Sommer-Aktion: 20% auf 10er-Abos bis Ende Juli" data-testid="marketing-ai-prompt" />
                <Button onClick={handleAi} disabled={aiBusy} className="bg-indigo-600 hover:bg-indigo-700 shrink-0" data-testid="marketing-ai-generate">
                  {aiBusy ? '✨…' : '✨ Erstellen'}
                </Button>
              </div>
            </div>

            {/* Presets – gruppiert nach Zielgruppe */}
            <div className="space-y-2.5">
              {PRESET_GROUPS.map((g) => (
                <div key={g.audience} data-testid={`preset-group-${g.audience}`}>
                  <p className="text-xs font-bold text-gray-700 mb-1.5">{g.label}</p>
                  <div className="flex flex-wrap gap-2">
                    {MARKETING_PRESETS.filter((p) => p.audience === g.audience).map((p) => (
                      <Button key={p.key} type="button" variant="outline" size="sm" onClick={() => applyPreset(p)} data-testid={`marketing-preset-${p.key}`}>
                        {p.icon} {p.label}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label>Betreff *</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="z.B. Neuigkeiten von StrongFit" data-testid="marketing-subject" />
            </div>

            <div className="space-y-2">
              <Label>Inhalt (HTML)</Label>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} className="w-full min-h-[200px] border rounded-md p-2 font-mono text-sm" placeholder="<h1>Hallo {first_name}!</h1>…" data-testid="marketing-body" />
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500">Platzhalter einfügen:</span>
                {PLACEHOLDERS.map((ph) => (
                  <button key={ph} type="button" onClick={() => insertPlaceholder(ph)} className="text-[11px] font-mono bg-gray-100 hover:bg-gray-200 rounded px-1.5 py-0.5" data-testid={`marketing-ph-${ph.replace(/[{}]/g, '')}`}>{ph}</button>
                ))}
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs">Buchungs-Link (für {'{booking_link}'})</Label>
                <Input value={bookingLink} onChange={(e) => setBookingLink(e.target.value)} data-testid="marketing-booking-link" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">🥂 Apéro-Datum (für {'{apero_date}'}, wenn genutzt)</Label>
                <Input value={aperoDate} onChange={(e) => setAperoDate(e.target.value)} placeholder="z.B. Samstag, 12. Juli, 17:00 Uhr" data-testid="marketing-apero-date" />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" onClick={() => setShowPreview(!showPreview)} data-testid="marketing-preview-toggle">
                {showPreview ? '👁️ Vorschau ausblenden' : '👁️ Vorschau anzeigen'}
              </Button>
            </div>
            {showPreview && body.trim() && (
              <div className="rounded-lg overflow-hidden border" data-testid="marketing-preview">
                <div className="px-4 py-2 bg-gray-100 text-xs text-gray-600 border-b">Vorschau (wie beim Empfänger) – Betreff: <strong>{subject || '(kein Betreff)'}</strong></div>
                <iframe title="Vorschau" className="w-full" style={{ height: 560, background: '#0a0a0a' }} srcDoc={renderMarketingPreview(body, 24, brandName, brandLogo)} />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* WhatsApp */}
      {sendWhatsapp && (
        <Card data-testid="marketing-whatsapp-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-green-600" /> WhatsApp-Vorlage</CardTitle>
            <CardDescription>Proaktive WhatsApp-Nachrichten brauchen eine von Meta genehmigte Vorlage.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <Label>Genehmigte Vorlage *</Label>
              <select className="w-full border rounded-md p-2 text-sm bg-white" value={waTemplateName} onChange={(e) => onSelectTemplate(e.target.value)} data-testid="marketing-wa-template">
                <option value="">Bitte Vorlage wählen</option>
                {waTemplates.map((t) => (<option key={t.id || t.name} value={t.name}>{t.name} ({t.language})</option>))}
              </select>
              {waTemplates.length === 0 && <p className="text-xs text-amber-600">Keine genehmigten WhatsApp-Vorlagen gefunden.</p>}
            </div>
            {waRequiredParams > 0 && (
              <div className="space-y-2" data-testid="marketing-wa-params">
                <Label className="text-xs">Platzhalter-Werte (Platzhalter wie {'{first_name}'} erlaubt)</Label>
                {Array.from({ length: waRequiredParams }).map((_, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-xs font-mono text-gray-500 w-10">{`{{${i + 1}}}`}</span>
                    <Input value={waParams[i] || ''} onChange={(e) => { const n = [...waParams]; n[i] = e.target.value; setWaParams(n); }} data-testid={`marketing-wa-param-${i}`} />
                  </div>
                ))}
              </div>
            )}
            {waBodyText && (
              <div>
                <Label className="text-xs mb-1 block">Vorschau</Label>
                <div style={{ background: '#0b141a', padding: 12, borderRadius: 10 }}>
                  <div style={{ background: '#005c4b', color: '#e9edef', padding: '8px 10px', borderRadius: 8, maxWidth: '85%', whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.5 }}>
                    {waBodyText.replace(/{{(\d+)}}/g, (_m, n) => waParams[Number(n) - 1] || `{{${n}}}`)}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* 4. Test & Senden */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-teal-600" /> 4. Testen & Senden</CardTitle>
          <CardDescription>Sende dir zuerst einen Test, dann die echte Kampagne an die gewählte Zielgruppe.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Test-E-Mail an</Label>
              <Input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="info@strongfit.ch" className="w-56" data-testid="marketing-test-email" />
            </div>
            <Button variant="outline" onClick={handleTest} disabled={testBusy} data-testid="marketing-test-send">
              {testBusy ? 'Sende…' : '📧 Test senden'}
            </Button>
          </div>
          <div className="flex items-center justify-between border-t pt-3">
            <p className="text-xs text-gray-500">
              Ziel: <strong>{audienceLabel}</strong> · {sendEmail ? `${emailCount} E-Mail` : ''}{sendEmail && sendWhatsapp ? ' · ' : ''}{sendWhatsapp ? `${phoneCount} WhatsApp` : ''}
            </p>
            <Button onClick={handleSend} disabled={sending} className="bg-teal-600 hover:bg-teal-700" data-testid="marketing-send-now">
              <Send className="w-4 h-4 mr-1.5" /> {sending ? 'Starte…' : 'Kampagne jetzt senden'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Verlauf */}
      <Card data-testid="marketing-history-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><History className="w-5 h-5 text-gray-500" /> Kampagnen-Verlauf</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-gray-500">Noch keine Kampagnen versendet.</p>
          ) : (
            <div className="space-y-2">
              {history.map((h) => (
                <div key={h.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 border rounded-lg bg-gray-50" data-testid={`marketing-history-${h.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">{h.started_at ? new Date(h.started_at).toLocaleString('de-CH') : ''}</span>
                      <Badge variant="outline" className="text-[10px]">{h.source}</Badge>
                      <Badge className={`text-[10px] ${h.status === 'done' ? 'bg-green-100 text-green-700' : h.status === 'error' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        {h.status === 'done' ? 'Fertig' : h.status === 'error' ? 'Fehler' : 'Läuft'}
                      </Badge>
                    </div>
                    <p className="text-sm font-medium truncate">{h.email_subject || h.whatsapp_template_name || '(kein Betreff)'}</p>
                  </div>
                  <div className="mt-2 sm:mt-0 flex gap-3 text-xs text-gray-600 shrink-0">
                    <span><strong>E-Mail:</strong> {h.email_sent ?? 0}</span>
                    <span><strong>WhatsApp:</strong> {h.whatsapp_sent ?? 0}</span>
                    <span><strong>Fehler:</strong> {h.failed ?? 0}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MarketingCampaignComposer;
